/**
 * VarianceAnalysis - Automatically flags significant variances between years
 * Updated to handle extended income statement structure with financial items
 * 
 * Now supports integration with audit materiality thresholds:
 * - Trivial threshold: Changes below this are filtered out
 * - Performance materiality: Changes above this are warnings
 * - Overall materiality: Changes above this are critical
 */

import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  AlertTriangle, 
  AlertCircle, 
  Info, 
  TrendingUp, 
  TrendingDown,
  CheckCircle,
  Settings2,
  Filter,
  SlidersHorizontal,
} from 'lucide-react';
import type { SIEProcessedData, VarianceIssue, IncomeStatementItem } from './types';
import { formatAmountWithDisplayFormat, determineOptimalUnit, formatPercent, calculatePercentChange, type DisplayUnit, type DisplayFormat } from './utils';
import type { MaterialityThresholds } from '@/lib/modules/types';

interface VarianceAnalysisProps {
  currentYear: SIEProcessedData;
  previousYear: SIEProcessedData;
  displayFormat?: DisplayFormat;
  thresholds?: MaterialityThresholds | null;
  auditId?: string;
}

// Format currency with Swedish formatting
const formatCurrency = (value: number): string => {
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1)} mkr`;
  } else if (value >= 1000) {
    return `${(value / 1000).toFixed(0)} tkr`;
  }
  return `${value.toFixed(0)} kr`;
};

export function VarianceAnalysis({ 
  currentYear, 
  previousYear, 
  displayFormat = 'auto',
  thresholds = null,
  auditId,
}: VarianceAnalysisProps) {
  // Extract threshold values with fallbacks
  const trivialThreshold = thresholds?.clearly_trivial_threshold?.value ?? 10000;
  const performanceThreshold = thresholds?.performance_materiality?.value;
  const overallThreshold = thresholds?.overall_materiality?.value;
  
  const hasConfiguredThresholds = !!(
    thresholds?.clearly_trivial_threshold?.value ||
    thresholds?.performance_materiality?.value ||
    thresholds?.overall_materiality?.value
  );

  // Calculate optimal unit for auto mode
  const autoUnit: DisplayUnit = useMemo(() => {
    const currIncome = currentYear.aggregated.incomeStatement;
    const prevIncome = previousYear.aggregated.incomeStatement;
    const allAmounts = [
      currIncome.totalRevenue, prevIncome.totalRevenue,
      currIncome.totalExpenses, prevIncome.totalExpenses,
      currIncome.netResult, prevIncome.netResult,
    ];
    return determineOptimalUnit(allAmounts);
  }, [currentYear, previousYear]);

  // Helper to format amounts
  const fmt = (amount: number) => formatAmountWithDisplayFormat(amount, displayFormat, autoUnit);

  const { issues, trivialFilteredCount } = useMemo(() => {
    const result: VarianceIssue[] = [];
    let trivialFiltered = 0;
    
    const currIncome = currentYear.aggregated.incomeStatement;
    const prevIncome = previousYear.aggregated.incomeStatement;

    // Helper to determine severity based on thresholds
    const determineSeverity = (absoluteChange: number, percentChange: number): 'critical' | 'warning' | 'info' => {
      if (overallThreshold && absoluteChange >= overallThreshold) return 'critical';
      if (performanceThreshold && absoluteChange >= performanceThreshold) return 'warning';
      if (Math.abs(percentChange) >= 50) return 'critical';
      if (Math.abs(percentChange) >= 25) return 'warning';
      return 'info';
    };

    // Helper to check account changes
    const checkAccountChange = (curr: IncomeStatementItem, prev: IncomeStatementItem | undefined, category: string) => {
      const currAmount = Math.abs(curr.amount);
      const prevAmount = prev ? Math.abs(prev.amount) : 0;
      const absoluteChange = Math.abs(currAmount - prevAmount);
      const percentChange = calculatePercentChange(currAmount, prevAmount);

      if (absoluteChange < trivialThreshold) { trivialFiltered++; return; }

      const meetsPercentThreshold = Math.abs(percentChange) >= 25 && prevAmount > 0;
      const meetsAmountThreshold = performanceThreshold && absoluteChange >= performanceThreshold;
      
      if (meetsPercentThreshold || meetsAmountThreshold) {
        result.push({
          severity: determineSeverity(absoluteChange, percentChange),
          account: curr.account,
          accountName: curr.name,
          description: `${category}: Konto ${curr.account} "${curr.name}" ${percentChange > 0 ? 'ökade' : 'minskade'} med ${Math.abs(percentChange).toFixed(0)}%`,
          currentValue: currAmount,
          previousValue: prevAmount,
          percentChange,
        });
      }
    };

    const checkNewAccount = (curr: IncomeStatementItem, prevMap: Map<string, IncomeStatementItem>, category: string) => {
      const amount = Math.abs(curr.amount);
      if (!prevMap.has(curr.account) && amount > trivialThreshold) {
        result.push({
          severity: 'info',
          account: curr.account,
          accountName: curr.name,
          description: `${category}: Nytt konto ${curr.account} "${curr.name}" med ${fmt(amount)}`,
          currentValue: amount,
          previousValue: 0,
          percentChange: 100,
        });
      }
    };

    const checkRemovedAccount = (prev: IncomeStatementItem, currMap: Map<string, IncomeStatementItem>, category: string) => {
      const amount = Math.abs(prev.amount);
      if (!currMap.has(prev.account) && amount > trivialThreshold) {
        result.push({
          severity: 'warning',
          account: prev.account,
          accountName: prev.name,
          description: `${category}: Konto ${prev.account} "${prev.name}" fanns inte innevarande år (föregående: ${fmt(amount)})`,
          currentValue: 0,
          previousValue: amount,
          percentChange: -100,
        });
      }
    };

    const processCategory = (currItems: IncomeStatementItem[] | undefined, prevItems: IncomeStatementItem[] | undefined, category: string) => {
      const safeCurrentItems = currItems || [];
      const safePrevItems = prevItems || [];
      const prevMap = new Map(safePrevItems.map(item => [item.account, item]));
      const currMap = new Map(safeCurrentItems.map(item => [item.account, item]));

      for (const curr of safeCurrentItems) {
        checkAccountChange(curr, prevMap.get(curr.account), category);
        checkNewAccount(curr, prevMap, category);
      }
      for (const prev of safePrevItems) {
        checkRemovedAccount(prev, currMap, category);
      }
    };

    processCategory(currIncome.revenue, prevIncome.revenue, 'Rörelseintäkter');
    processCategory(currIncome.expenses, prevIncome.expenses, 'Rörelsekostnader');
    processCategory(currIncome.financialIncome, prevIncome.financialIncome, 'Finansiella intäkter');
    processCategory(currIncome.financialExpenses, prevIncome.financialExpenses, 'Finansiella kostnader');
    processCategory(currIncome.appropriations, prevIncome.appropriations, 'Bokslutsdispositioner');
    processCategory(currIncome.taxes, prevIncome.taxes, 'Skatt');

    // Operating margin change
    const currOperatingResult = currIncome.operatingResult ?? (currIncome.totalRevenue - currIncome.totalExpenses);
    const prevOperatingResult = prevIncome.operatingResult ?? (prevIncome.totalRevenue - prevIncome.totalExpenses);
    const currOperatingMargin = currIncome.totalRevenue > 0 ? (currOperatingResult / currIncome.totalRevenue) * 100 : 0;
    const prevOperatingMargin = prevIncome.totalRevenue > 0 ? (prevOperatingResult / prevIncome.totalRevenue) * 100 : 0;
    const operatingMarginChange = currOperatingMargin - prevOperatingMargin;

    if (Math.abs(operatingMarginChange) >= 3) {
      result.push({
        severity: Math.abs(operatingMarginChange) >= 5 ? 'critical' : 'warning',
        description: `Rörelsemarginalen ${operatingMarginChange > 0 ? 'ökade' : 'sjönk'} från ${prevOperatingMargin.toFixed(1)}% till ${currOperatingMargin.toFixed(1)}%`,
        currentValue: currOperatingMargin,
        previousValue: prevOperatingMargin,
        percentChange: operatingMarginChange,
      });
    }

    // Total revenue change
    const revenueChange = calculatePercentChange(currIncome.totalRevenue, prevIncome.totalRevenue);
    if (Math.abs(revenueChange) >= 15) {
      result.push({
        severity: Math.abs(revenueChange) >= 30 ? 'critical' : 'warning',
        description: `Total omsättning ${revenueChange > 0 ? 'ökade' : 'minskade'} med ${Math.abs(revenueChange).toFixed(0)}%`,
        currentValue: currIncome.totalRevenue,
        previousValue: prevIncome.totalRevenue,
        percentChange: revenueChange,
      });
    }

    // Financial net change
    const currFinancialNet = currIncome.financialNet ?? 0;
    const prevFinancialNet = prevIncome.financialNet ?? 0;
    const financialNetChange = Math.abs(currFinancialNet - prevFinancialNet);
    
    if (financialNetChange > Math.max(trivialThreshold, 100000)) {
      const financialNetPercent = calculatePercentChange(currFinancialNet, prevFinancialNet);
      if (Math.abs(financialNetPercent) >= 25) {
        result.push({
          severity: 'warning',
          description: `Finansnetto ${currFinancialNet > prevFinancialNet ? 'ökade' : 'minskade'} markant (${fmt(currFinancialNet - prevFinancialNet)})`,
          currentValue: currFinancialNet,
          previousValue: prevFinancialNet,
          percentChange: financialNetPercent,
        });
      }
    }

    const severityOrder = { critical: 0, warning: 1, info: 2 };
    return {
      issues: result.sort((a, b) => severityOrder[a.severity] - severityOrder[b.severity]),
      trivialFilteredCount: trivialFiltered,
    };
  }, [currentYear, previousYear, displayFormat, autoUnit, trivialThreshold, performanceThreshold, overallThreshold]);

  const criticalCount = issues.filter(i => i.severity === 'critical').length;
  const warningCount = issues.filter(i => i.severity === 'warning').length;
  const infoCount = issues.filter(i => i.severity === 'info').length;

  const getSeverityIcon = (severity: VarianceIssue['severity']) => {
    switch (severity) {
      case 'critical': return <AlertCircle className="h-4 w-4" />;
      case 'warning': return <AlertTriangle className="h-4 w-4" />;
      default: return <Info className="h-4 w-4" />;
    }
  };

  const getSeverityVariant = (severity: VarianceIssue['severity']) => {
    return severity === 'critical' ? 'destructive' : 'default';
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" />
            Automatisk avvikelseanalys
          </CardTitle>
          <CardDescription>Identifierade avvikelser baserat på regelbaserad analys</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Badge variant="destructive">{criticalCount}</Badge>
              <span className="text-sm text-muted-foreground">Kritiska</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-warning/20 text-warning-foreground border-warning/30">{warningCount}</Badge>
              <span className="text-sm text-muted-foreground">Varningar</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary">{infoCount}</Badge>
              <span className="text-sm text-muted-foreground">Information</span>
            </div>
            {trivialFilteredCount > 0 && (
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-muted-foreground">
                  <Filter className="h-3 w-3 mr-1" />{trivialFilteredCount}
                </Badge>
                <span className="text-sm text-muted-foreground">Triviala (filtrerade)</span>
              </div>
            )}
          </div>
          
          {hasConfiguredThresholds ? (
            <div className="flex gap-4 text-xs text-muted-foreground border-t pt-3 flex-wrap items-center justify-between">
              <div className="flex gap-4 flex-wrap items-center">
                <span className="flex items-center gap-1"><Settings2 className="h-3 w-3" />Revisionströsklar:</span>
                {thresholds?.clearly_trivial_threshold?.value && <span>Trivialitet: <strong>{formatCurrency(thresholds.clearly_trivial_threshold.value)}</strong></span>}
                {thresholds?.performance_materiality?.value && <span>Prestanda: <strong>{formatCurrency(thresholds.performance_materiality.value)}</strong></span>}
                {thresholds?.overall_materiality?.value && <span>Väsentlighet: <strong>{formatCurrency(thresholds.overall_materiality.value)}</strong></span>}
              </div>
              {auditId && (
                <Button variant="ghost" size="sm" className="h-7 text-xs" asChild>
                  <Link to={`/cases/${auditId}?tab=config`}><SlidersHorizontal className="h-3 w-3 mr-1" />Ändra trösklar</Link>
                </Button>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2 text-xs text-muted-foreground border-t pt-3 justify-between">
              <div className="flex items-center gap-2">
                <Info className="h-3 w-3" />
                <span>Använder standardregler (25%/50% förändring, {formatCurrency(trivialThreshold)} trivialitetsgräns).</span>
              </div>
              {auditId && (
                <Button variant="outline" size="sm" className="h-7 text-xs" asChild>
                  <Link to={`/cases/${auditId}?tab=config`}><SlidersHorizontal className="h-3 w-3 mr-1" />Konfigurera trösklar</Link>
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {issues.length === 0 ? (
        <Alert>
          <CheckCircle className="h-4 w-4" />
          <AlertTitle>Inga signifikanta avvikelser</AlertTitle>
          <AlertDescription>Inga betydande förändringar upptäcktes mellan de två räkenskapsåren.</AlertDescription>
        </Alert>
      ) : (
        <div className="space-y-3">
          {issues.map((issue, index) => (
            <Alert key={index} variant={getSeverityVariant(issue.severity) as 'default' | 'destructive'}>
              {getSeverityIcon(issue.severity)}
              <AlertTitle className="flex items-center gap-2">
                {issue.account && <span className="font-mono text-xs bg-muted px-1 rounded">{issue.account}</span>}
                {issue.accountName || 'Övergripande'}
              </AlertTitle>
              <AlertDescription className="mt-2">
                <p>{issue.description}</p>
                <div className="flex gap-4 mt-2 text-xs">
                  <span className="flex items-center gap-1">
                    <span className="text-muted-foreground">Nuvarande:</span>
                    <span className="font-medium">{issue.percentChange === issue.currentValue ? `${issue.currentValue.toFixed(1)}%` : fmt(issue.currentValue)}</span>
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="text-muted-foreground">Föregående:</span>
                    <span className="font-medium">{issue.percentChange === issue.previousValue ? `${issue.previousValue.toFixed(1)}%` : fmt(issue.previousValue)}</span>
                  </span>
                  <span className="flex items-center gap-1">
                    {issue.percentChange >= 0 ? <TrendingUp className="h-3 w-3 text-success" /> : <TrendingDown className="h-3 w-3 text-destructive" />}
                    <span className={issue.percentChange >= 0 ? 'text-success' : 'text-destructive'}>{formatPercent(issue.percentChange)}</span>
                  </span>
                </div>
              </AlertDescription>
            </Alert>
          ))}
        </div>
      )}
    </div>
  );
}
